<script setup lang="ts">
  import { ONE_WAY, RETURN } from '@/components/clp/constants'
  import type { IDestinationItem, TDirection } from '@/components/clp/types'
  import type { ClpUploadStoryblok } from '@/storyblok/component-types-sb'
  import useCLPDataLayer from '@/composables/data-layers/useCLPDataLayer'
  import { cva } from 'class-variance-authority'

  const runtimeConfig = useRuntimeConfig()
  const props = withDefaults(
    defineProps<{
      item: IDestinationItem
      isActive: boolean
      cherryIcon?: ClpUploadStoryblok['cherryIcon']
    }>(),
    {
      cherryIcon: 'common/cherry'
    }
  )

  const selectedDirection = ref<TDirection>(ONE_WAY)
  const selectDirection = (direction: TDirection) => {
    selectedDirection.value = direction
    onDirectionChange(direction)
  }

  const bgImageSrc = computed(
    () => `${runtimeConfig.public.btWebAssets}/CLP_bigger_pictures_716x478/${props.item.destinCode.toLowerCase()}.webp`
  )

  const { pushCLPWithOriginDataLayer } = useCLPDataLayer()
  function onDirectionChange(newVal?: string) {
    if (!newVal) {
      return
    }

    pushCLPWithOriginDataLayer({
      generic_event_name: 'trip_type_selection',
      event_category: 'main_section',
      trip_type: newVal === ONE_WAY ? 'OW' : 'RT',
      destination_airport_code: props.item.destinCode,
      price_from: newVal === ONE_WAY ? props.item.priceOW : props.item.priceRT
    })
  }

  const REDUCED_PRICE_LABEL = 'clp.label.label5'

  const labels = computed(
    () =>
      props.item.labels
        ?.split(',')
        .map((label) => label.trim())
        .filter(Boolean) ?? []
  )

  const { isDesktop } = useBTBreakpoints()
  const image = useImage()

  const labelClasses = cva('w-fit text-nowrap rounded-md px-2 py-0.5 text-sm font-medium', {
    variants: {
      isReducedPrice: {
        true: 'bg-bt-grabout-red text-white',
        false: 'bg-bt-green'
      }
    },
    defaultVariants: {
      isReducedPrice: false
    }
  })
</script>
<template>
  <div
    tabindex="0"
    class="clp-destination-item group relative"
  >
    <div class="w-full rounded-md bg-white shadow-md">
      <div class="px-4 pt-4 md:p-4">
        <div class="relative">
          <div class="relative mb-4 max-h-50 w-full overflow-hidden rounded-sm">
            <NuxtImg
              class="block h-50 w-full object-cover transition-transform duration-500 group-focus-within:scale-110 group-hover:scale-110"
              :src="bgImageSrc"
              :placeholder="image('/placeholder-img.png', { h: 200, f: 'webp', q: 70 })"
              loading="lazy"
              format="webp"
              :alt="item.destinCity"
            />
            <div
              v-if="item.isSoldOut"
              class="absolute bottom-0 left-0 w-full bg-bt-blue-dark/70 px-2 py-1"
            >
              <p class="mb-0 text-white text-body-3">
                {{ $t('clp.soldOut') }}
              </p>
            </div>
          </div>
          <div class="absolute left-2 top-2">
            <div class="relative flex flex-col gap-2">
              <div
                v-for="label in labels"
                :key="label"
                :class="
                  labelClasses({
                    isReducedPrice: label === REDUCED_PRICE_LABEL
                  })
                "
              >
                {{ $t(label) }}
              </div>
            </div>
          </div>
        </div>
        <div class="mb-4">
          <div class="mb-1 text-lg font-medium leading-8 md:mb-2">{{ item.destinCity }}</div>
          <div class="text-secondary">
            <span class="rounded-sm border border-border px-2 py-0.5">{{ item.destinCode }}</span>
            <span class="ml-2 text-sm">{{ item.destinCountry }}</span>
          </div>
        </div>
        <div
          v-if="isDesktop"
          class="justify-between opacity-100 transition-opacity duration-75 md:flex md:group-focus-within:opacity-0 md:group-hover:opacity-0"
        >
          <div class="text-sm">{{ item.priceOW ? $t('clp.oneway') : $t('clp.return') }}</div>
          <div
            class="flex"
            :class="{ 'text-secondary': item.isSoldOut }"
          >
            <span class="mr-1 text-xs">{{ $t('clp.priceFrom') }}</span>
            <span class="text-lg">{{ item.priceOW ?? item.priceRT }}€</span>
            <Icon
              v-if="!item.isSoldOut"
              :name="cherryIcon"
              filled
            />
          </div>
        </div>
      </div>
      <div class="relative">
        <div class="price-wrapper">
          <div class="price-wrapper__animation">
            <ClpPriceItem
              v-if="item.priceOW"
              :price="item.priceOW"
              :direction="ONE_WAY"
              :destination="item.destinCity"
              :cherry-icon="cherryIcon"
              :is-selected="selectedDirection === ONE_WAY"
              :selected-direction="selectedDirection"
              :is-sold-out="item.isSoldOut"
              @select-direction="selectDirection"
            />
            <ClpPriceItem
              v-if="item.priceRT"
              :price="item.priceRT"
              :direction="RETURN"
              :destination="item.destinCity"
              :cherry-icon="cherryIcon"
              :is-selected="selectedDirection === RETURN"
              :selected-direction="selectedDirection"
              :is-sold-out="item.isSoldOut"
              @select-direction="selectDirection"
            />
            <ClpDestinationButton
              :item="item"
              :selected-direction="selectedDirection"
              button-category="main_section"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
  .price-wrapper {
    transition: height 300ms linear;

    @apply w-full;

    @screen md {
      @apply pointer-events-none absolute -top-3 z-5 h-0 rounded-b-md bg-white;

      box-shadow: 0px 4px 32px 0px rgba(21, 38, 73, 0.12);

      .group:hover &,
      .group:focus-within & {
        @apply pointer-events-auto h-44;
      }
    }

    &__animation {
      transition:
        opacity 300ms linear,
        height 300ms linear;

      @apply px-4 pb-4;

      @screen md {
        @apply relative -top-6 h-0 overflow-hidden rounded-b-md bg-white opacity-0;

        .group:hover &,
        .group:focus-within & {
          @apply h-50 opacity-100;
        }
      }
    }
  }
</style>
